import React from 'react';
import { MembershipItem, MEMBERSHIP_ITEM_TYPES, MembershipItemTypeValue } from '@spike/membership-model';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Service from 'model/Service';
import { useItemSelection, UseItemSelectionReturn } from 'hooks/useItemSelection';
import { toMembershipItem } from '../utils/membershipItemUtils';
import { useMembershipItemCatalogs } from './useMembershipItemCatalogs';

interface UseMembershipItemDrawersConfig {
    items: MembershipItem[];
    excludedItemIDs: string[];
    marketplaceID: number;
    onChange: (items: MembershipItem[]) => void;
}

export interface DrawerState<T> {
    drawer: UseItemSelectionReturn<T>;
    /** Catalog items available in this drawer (excludedItemIDs already filtered out) */
    availableItems: T[];
    /** Items from the current table that belong to this category (for pre-selection) */
    currentItems: T[];
    /** Full unfiltered catalog (used to determine if the category has items at all) */
    catalogItems: T[];
}

export interface UseMembershipItemDrawersReturn {
    openDrawerForType: (type: MembershipItemTypeValue) => void;
    grooming: DrawerState<Service>;
    daycare: DrawerState<Service>;
    boarding: DrawerState<Service>;
    walking: DrawerState<Service>;
    transportation: DrawerState<Service>;
    training: DrawerState<Service>;
    addons: DrawerState<AddOnService>;
    products: DrawerState<Product>;
}

const nameFilter = (item: { name: string }, query: string) => item.name.toLowerCase().includes(query.toLowerCase());

export const useMembershipItemDrawers = ({
    items,
    excludedItemIDs,
    marketplaceID,
    onChange
}: UseMembershipItemDrawersConfig): UseMembershipItemDrawersReturn => {
    const {
        groomingCatalog,
        daycareCatalog,
        boardingCatalog,
        walkingCatalog,
        transportationCatalog,
        trainingCatalog,
        addonsCatalog,
        productsCatalog,
        availableGrooming,
        availableDaycare,
        availableBoarding,
        availableWalking,
        availableTransportation,
        availableTraining,
        availableAddons,
        availableProducts,
        currentGrooming,
        currentDaycare,
        currentBoarding,
        currentWalking,
        currentTransportation,
        currentTraining,
        currentAddons,
        currentProducts,
        normalizedItems
    } = useMembershipItemCatalogs({ items, excludedItemIDs });

    // ─── onSelectionChange factories ───────────────────────────────────────────

    const itemsRef = React.useRef(normalizedItems);
    itemsRef.current = normalizedItems;

    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;

    const makeOnChange = React.useCallback(
        (itemType: MembershipItemTypeValue) => (selected: Array<Service | AddOnService | Product>) => {
            const newItems = selected.map(s => toMembershipItem(s, itemType, marketplaceID, false));
            const otherItems = itemsRef.current.filter(i => i.itemType !== itemType);
            onChangeRef.current([...otherItems, ...newItems]);
        },
        [marketplaceID]
    );

    // ─── useItemSelection instances ────────────────────────────────────────────

    const groomingDrawer = useItemSelection<Service>({
        items: availableGrooming,
        selectedItems: currentGrooming,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE),
        filterFn: nameFilter
    });

    const daycareDrawer = useItemSelection<Service>({
        items: availableDaycare,
        selectedItems: currentDaycare,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE),
        filterFn: nameFilter
    });

    const boardingDrawer = useItemSelection<Service>({
        items: availableBoarding,
        selectedItems: currentBoarding,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE),
        filterFn: nameFilter
    });

    const walkingDrawer = useItemSelection<Service>({
        items: availableWalking,
        selectedItems: currentWalking,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE),
        filterFn: nameFilter
    });

    const transportationDrawer = useItemSelection<Service>({
        items: availableTransportation,
        selectedItems: currentTransportation,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE),
        filterFn: nameFilter
    });

    const trainingDrawer = useItemSelection<Service>({
        items: availableTraining,
        selectedItems: currentTraining,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE),
        filterFn: nameFilter
    });

    const addonsDrawer = useItemSelection<AddOnService>({
        items: availableAddons,
        selectedItems: currentAddons,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.ADD_ON),
        filterFn: nameFilter
    });

    const productsDrawer = useItemSelection<Product>({
        items: availableProducts,
        selectedItems: currentProducts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.PRODUCT),
        filterFn: nameFilter
    });

    // ─── openDrawerForType dispatcher ──────────────────────────────────────────

    const openDrawerForType = React.useCallback(
        (type: MembershipItemTypeValue) => {
            switch (type) {
                case MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE:
                    groomingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE:
                    daycareDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE:
                    boardingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE:
                    walkingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE:
                    transportationDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE:
                    trainingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.ADD_ON:
                    addonsDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.PRODUCT:
                    productsDrawer.openDrawer();
                    break;
            }
        },
        [
            groomingDrawer,
            daycareDrawer,
            boardingDrawer,
            walkingDrawer,
            transportationDrawer,
            trainingDrawer,
            addonsDrawer,
            productsDrawer
        ]
    );

    return {
        openDrawerForType,
        grooming: {
            drawer: groomingDrawer,
            availableItems: availableGrooming,
            currentItems: currentGrooming,
            catalogItems: groomingCatalog
        },
        daycare: {
            drawer: daycareDrawer,
            availableItems: availableDaycare,
            currentItems: currentDaycare,
            catalogItems: daycareCatalog
        },
        boarding: {
            drawer: boardingDrawer,
            availableItems: availableBoarding,
            currentItems: currentBoarding,
            catalogItems: boardingCatalog
        },
        walking: {
            drawer: walkingDrawer,
            availableItems: availableWalking,
            currentItems: currentWalking,
            catalogItems: walkingCatalog
        },
        transportation: {
            drawer: transportationDrawer,
            availableItems: availableTransportation,
            currentItems: currentTransportation,
            catalogItems: transportationCatalog
        },
        training: {
            drawer: trainingDrawer,
            availableItems: availableTraining,
            currentItems: currentTraining,
            catalogItems: trainingCatalog
        },
        addons: {
            drawer: addonsDrawer,
            availableItems: availableAddons,
            currentItems: currentAddons,
            catalogItems: addonsCatalog
        },
        products: {
            drawer: productsDrawer,
            availableItems: availableProducts,
            currentItems: currentProducts,
            catalogItems: productsCatalog
        }
    };
};
